/**
 * Money primitive for Totem.
 *
 * Internal representation is integer cents stored as `bigint` to avoid any IEEE-754 drift.
 * All financial code MUST go through these helpers — never use `Number` arithmetic on prices,
 * taxes, discounts, abonos or totals (rule §22 of `prompts/02_DEVELOPMENT_RULES_EN.md`).
 */

export type CurrencyCode = 'MXN' | 'USD';

export interface Money {
  readonly cents: bigint;
  readonly currency: CurrencyCode;
}

export const MXN_ZERO: Money = Object.freeze({ cents: 0n, currency: 'MXN' });

export function moneyOfCents(cents: bigint | number, currency: CurrencyCode = 'MXN'): Money {
  const value = typeof cents === 'bigint' ? cents : BigInt(Math.trunc(cents));
  return Object.freeze({ cents: value, currency });
}

/**
 * Build a Money from a major-unit decimal string ("123.45"). Rejects floats.
 * Use this only at the system boundary (env, parser) — not inside calculations.
 */
export function moneyOfMajorString(amount: string, currency: CurrencyCode = 'MXN'): Money {
  const trimmed = amount.trim();
  if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) {
    throw new RangeError(`moneyOfMajorString: invalid amount '${amount}'`);
  }
  const negative = trimmed.startsWith('-');
  const body = negative ? trimmed.slice(1) : trimmed;
  const [whole, frac = ''] = body.split('.');
  const padded = (frac + '00').slice(0, 2);
  const cents = BigInt(whole + padded) * (negative ? -1n : 1n);
  return moneyOfCents(cents, currency);
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new TypeError(`Money currency mismatch: ${a.currency} vs ${b.currency}`);
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return moneyOfCents(a.cents + b.cents, a.currency);
}

export function sub(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return moneyOfCents(a.cents - b.cents, a.currency);
}

export function negate(a: Money): Money {
  return moneyOfCents(-a.cents, a.currency);
}

/**
 * Multiply a Money by an integer quantity (e.g. line subtotal = unit_price * qty).
 */
export function mulInt(a: Money, qty: bigint | number): Money {
  const q = typeof qty === 'bigint' ? qty : BigInt(Math.trunc(qty));
  return moneyOfCents(a.cents * q, a.currency);
}

/**
 * Apply a rational rate `numer / denom` (e.g. tax 16% → 16/100, discount 12.5% → 125/1000).
 * Rounds half-to-even (banker's rounding) at integer cents.
 */
export function mulRate(
  a: Money,
  rate: { numer: bigint | number; denom: bigint | number },
): Money {
  const n = typeof rate.numer === 'bigint' ? rate.numer : BigInt(Math.trunc(rate.numer));
  const d = typeof rate.denom === 'bigint' ? rate.denom : BigInt(Math.trunc(rate.denom));
  if (d === 0n) throw new RangeError('mulRate: denom must not be zero');
  const product = a.cents * n;
  return moneyOfCents(divRoundHalfEven(product, d), a.currency);
}

/** Half-to-even integer division for bigints (banker's rounding). */
export function divRoundHalfEven(num: bigint, den: bigint): bigint {
  if (den === 0n) throw new RangeError('divRoundHalfEven: den must not be zero');
  const sign = (num < 0n) !== (den < 0n) ? -1n : 1n;
  const a = num < 0n ? -num : num;
  const b = den < 0n ? -den : den;
  const q = a / b;
  const r = a % b;
  const twice_r = r * 2n;
  if (twice_r < b) return sign * q;
  if (twice_r > b) return sign * (q + 1n);
  // Exactly half → round to even
  return sign * (q % 2n === 0n ? q : q + 1n);
}

export function eq(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.cents === b.cents;
}

export function gt(a: Money, b: Money): boolean {
  assertSameCurrency(a, b);
  return a.cents > b.cents;
}

export function lt(a: Money, b: Money): boolean {
  assertSameCurrency(a, b);
  return a.cents < b.cents;
}

export function isZero(a: Money): boolean {
  return a.cents === 0n;
}

export function isNegative(a: Money): boolean {
  return a.cents < 0n;
}

/**
 * Format for UI display. UI layer should always use this — never `.toFixed()`.
 */
export function format(money: Money, locale = 'es-MX'): string {
  const negative = money.cents < 0n;
  const abs = negative ? -money.cents : money.cents;
  const major = abs / 100n;
  const minor = abs % 100n;
  const minor_str = minor.toString().padStart(2, '0');
  const formatter = new Intl.NumberFormat(locale, { useGrouping: true });
  const major_str = formatter.format(major);
  const symbol = money.currency === 'MXN' ? '$' : '$';
  return `${negative ? '-' : ''}${symbol}${major_str}.${minor_str} ${money.currency}`;
}

/**
 * Render an integer-cents value as a major-unit decimal string ("12345" → "123.45").
 * Pure formatting — used by tickets/invoicing/dashboard to serialize stored amounts
 * without performing any financial calculation.
 */
export function centsToMajorString(cents: bigint | string | number, decimals = 2): string {
  const c =
    typeof cents === 'bigint'
      ? cents
      : typeof cents === 'number'
        ? BigInt(Math.trunc(cents))
        : BigInt(cents);
  const negative = c < 0n;
  const abs = negative ? -c : c;
  const whole = abs / 100n;
  const frac = abs % 100n;
  const frac_str = frac.toString().padStart(2, '0').slice(0, decimals).padEnd(decimals, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${frac_str}`;
}

/**
 * Render a stored fraction (numerator/denominator pair) as a six-decimal
 * SAT-compliant tax-rate string. Pure formatting — no financial calculation.
 */
export function rateToDecimalString(
  numerator: bigint | string | number,
  denominator: bigint | string | number,
): string {
  const num =
    typeof numerator === 'bigint'
      ? numerator
      : BigInt(typeof numerator === 'string' && numerator !== '' ? numerator : 0);
  const den_raw =
    typeof denominator === 'bigint'
      ? denominator
      : BigInt(typeof denominator === 'string' && denominator !== '' ? denominator : 1);
  const den = den_raw === 0n ? 1n : den_raw;
  const scaled = (num * 1_000_000n) / den;
  const whole = scaled / 1_000_000n;
  const frac = scaled % 1_000_000n;
  return `${whole.toString()}.${frac.toString().padStart(6, '0')}`;
}

/** Convert to plain JSON-safe shape (cents-as-string for transport). */
export interface MoneyDto {
  cents: string;
  currency: CurrencyCode;
}

export function toDto(money: Money): MoneyDto {
  return { cents: money.cents.toString(), currency: money.currency };
}

export function fromDto(dto: MoneyDto): Money {
  return moneyOfCents(BigInt(dto.cents), dto.currency);
}
